import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { environment as ENV } from 'src/environments/environment';
import { CommonService } from './shared/services/common.service';
import { select, Store } from '@ngrx/store';
import { filter, takeUntil } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { LoadingScreenComponent } from './shared/components/loading-screen/loading-screen.component';
import { isLoading } from './shared/core/shared.selectors';
import { MatDialog as MatDialog, MatDialogRef as MatDialogRef } from '@angular/material/dialog';
import * as fromRoot from 'src/app/state/app.state';
import { ClearLoading, FetchAppDataRequest } from './shared/core/shared.actions';
import { AuthService } from './shared/services/auth.service';
import { AnalyticsService } from './shared/services/analytics.service';
import { AppUpdateService } from './shared/services/app-update.service';
import * as Sentry from '@sentry/angular';
import { redactFreeText } from './shared/helpers/analytics-redact';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  title = 'Sitenotes';
  isLoadingScreenOpen = false;
  isLoadingData = [];
  private readonly onDestroy: Subject<any> = new Subject<any>();

  constructor(
    public commonService: CommonService,
    private cdRef: ChangeDetectorRef,
    private store: Store<fromRoot.State>,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<LoadingScreenComponent>,
    private authService: AuthService,
    private analyticsService: AnalyticsService,
    private appUpdateService: AppUpdateService,
    private router: Router,
  ) {
    this.store.dispatch(FetchAppDataRequest());
    this.store.dispatch(ClearLoading());
  }

  ngOnInit(): void {
    console.log(`Running ${ENV.APP_NAME} v${ENV.APP_VERSION}`);

    // Refresh the login-time account cache (org flags, roles, abilities) so
    // server-side changes reach long-lived sessions without a re-login
    this.authService.refreshCurrentUser();

    this.analyticsService.init();
    this.appUpdateService.start();

    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.onDestroy)
      )
      .subscribe((event: NavigationEnd) => {
        // Search text typed into the app (e.g. the drawings as-built search, abQ) never reaches analytics
        this.analyticsService.capture('$pageview', {
          path: redactFreeText(event.urlAfterRedirects),
          url: redactFreeText(window.location.href),
        });
      });

    // Set Sentry user context if already logged in
    const currentUser = this.authService.getCurrentUser();
    if (currentUser?.id) {
      Sentry.setUser({ id: currentUser.id });
      this.analyticsService.identify(currentUser, currentUser.organization);
    }

    this.store
      .pipe(
        select(isLoading),
        // debounceTime(10),
        takeUntil(this.onDestroy),
      )
      .subscribe(data => {
        this.isLoadingData = data;
        this.handleLoadingScreen();
      });
  }

  handleLoadingScreen() {
    if (this.isLoadingData.length && !this.isLoadingScreenOpen) {
      this.isLoadingScreenOpen = true;
      this.dialog.open(LoadingScreenComponent, {
        backdropClass: 'loader-backdrop',
        panelClass: 'loader-panel',
      });
    }

    if (!this.isLoadingData.length) {
      this.isLoadingScreenOpen = false;
      this.commonService.closeLoadingScreen.next(null);
    }
  }

  ngOnDestroy() {
    this.onDestroy.next(null);
    this.onDestroy.complete();
  }
}
